import { QESButton } from "@app/shared/qes-standards/Button"
import { PatternOfCareProviderModel, PatternOfCareProvidersTableModel, PatternOfCareProviderLookupSearchModel, PatternOfCareProviderSpecialtyModel } from "../interfaces";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { isZipCodeInputValid } from "@app/shared/utility";
import PatternOfCareProvidersService from "../patternOfCareProvidersService";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ColumnConfig, WafflesPaginationProps } from "@app/shared/waffles";
import { toast } from "react-toastify";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { PatternOfCareProviderLookupSearchBox } from "./PatternOfCareProviderLookupSearchBox";
import { buildTableData, customSort } from "../patternOfCareSharedMethods";
import { UserContext } from "@app/user";
import { FeatureFlagContext, Features } from "@app/feature-flags";

const maxLengths: { [key: string]: number } = {
  address: 255,
  city: 255,
  firstName: 255,
  lastName: 255,
  npi: 20,
  organizationName: 255,
  stateAbbreviation: 2,
  tin: 20,
  zip: 5,
};

const columnConfig: ColumnConfig = {
  address: {
    displayText: "Address",
    sizeContentToFit: true,
    testId: "address",
  },
  checkBoxIsDisabled: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  name: {
    displayText: "Name",
    sizeContentToFit: true,
    testId: "name",
  },
  specialty: {
    displayText: "Specialty",
    sizeContentToFit: true,
    testId: "specialty",
  }
}

export const PatternOfCareProviderLookup: FC = () => {
  const user = useContext(UserContext);
  const projectContext = useContext(CountyProjectContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const patternOfCareService = useRef(new PatternOfCareProvidersService()).current;
  const searchProvidersText = "To view providers please search using the fields above.";
  const [emptyTableMessage, setEmptyTableMessage] = useState(searchProvidersText);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [providerDetails, setProviderDetails] = useState<PatternOfCareProviderModel[]>([]);
  const [tableData, setTableData] = useState<PatternOfCareProvidersTableModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [isNameSearchEnabled, setIsNameSearchEnabled] = useState(featureFlags.has(Features.PATTERN_OF_CARE_NAME_SEARCH));
  const [skip, setSkip] = useState(0);
  const take = useRef(100).current;
  const [searchState, setSearchState] = useState<PatternOfCareProviderLookupSearchModel>({
    address: "",
    city: "",
    firstName: "",
    lastName: "",
    npi: "",
    organizationName: "",
    state: "",
    tin: "",
    zip: "",
  });

  useEffect(() => {
    setIsNameSearchEnabled(featureFlags.has(Features.PATTERN_OF_CARE_NAME_SEARCH));
  }, [featureFlags]);

  const handleFieldUpdate = (key: keyof PatternOfCareProviderLookupSearchModel, value: string) => {
    const newValue = value.substring(0, maxLengths[key]);

    if (key === "zip") {
      if (!isZipCodeInputValid(value)) {
        return;
      }
    } else if (key === "state") {
      if (!(/^[a-zA-Z]{0,2}$/).test(value)) {
        return;
      }
    }

    const newSearchState = {
      ...searchState,
      [key]: newValue,
    };
    setSearchState(newSearchState);
  };

  const handleSearch = () => {
    setIsLoading(true);
    patternOfCareService.providerLookup(
      projectContext.project.projectId,
      projectContext.runConfiguration.projectRunConfigurationId,
      projectContext.adequacyResult.details[0].regionSetId,
      projectContext.regionFilters.specialty,
      searchState
    ).then((response) => {
      setProviderDetails(response);
      const newTableData = buildTableData(response, user);
      setTableData(newTableData);
      setEmptyTableMessage(undefined);
      setSelectedProviders([]);
      setSkip(0);
      setIsFiltered(true);
    }, (errorResponse) => {
      // we expect an error message if there are 0 or > {CustomSettings.ProviderLookupCount} providers 
      if (!!errorResponse.responseText) {
        setEmptyTableMessage(errorResponse.responseText);
      }
      setProviderDetails([]);
      setTableData([]);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const handleClear = () => {
    setSearchState({
      address: "",
      city: "",
      firstName: "",
      lastName: "",
      npi: "",
      organizationName: "",
      state: "",
      tin: "",
      zip: "",
    });
    setProviderDetails([]);
    setTableData([]);
    setEmptyTableMessage(searchProvidersText);
    setIsFiltered(false);
  };

  const handleCheckboxChange = (selectedKeys: string[]) => {
    setSelectedProviders(selectedKeys);
  }

  const handleAppendClick = () => {
    const providerCreationModels = selectedProviders.map<PatternOfCareProviderSpecialtyModel>(p => {
      const providerId = parseInt(p, 10);
      const provider = providerDetails.find(detail => detail.providerId == providerId);
      return {
        address: provider.address,
        address2: provider.address2,
        city: provider.city,
        geoInfo: provider.geoInfo,
        latitude: provider.latitude,
        longitude: provider.longitude,
        npi: provider.npi,
        providerId,
        specialtyCode: provider.specialtyCode,
        state: provider.state,
        zip: provider.zip
      }
    });
    patternOfCareService.appendProviders(
      providerCreationModels,
      projectContext.project.projectId,
      projectContext.runConfiguration.projectRunConfigurationId,
      projectContext.adequacyResult.details[0].regionSetId,
      projectContext.regionFilters.specialty,
    ).then(() => {
      const newProviders = providerDetails.filter((pd) => !selectedProviders.includes(pd.providerId.toString()));
      const newTableData = buildTableData(newProviders, user);
      setProviderDetails(newProviders);
      setTableData(newTableData);
      setSelectedProviders([]);
      toast("Providers successfully added to Appended Providers", {
        containerId: "z-index-1030",
        type: "success"
      });
    }, () => {
      toast("An error occurred trying to Append Providers. Please try again.", {
        containerId: "z-index-1030",
        type: "error"
      });
    });
  }

  const bulkActionBar = (
    <QESButton
      qesType="secondary"
      qesTestId="pattern-of-care-panel__append-providers"
      disabled={selectedProviders.length === 0}
      onClick={handleAppendClick}
    >
      Append to HSD Tables
    </QESButton>
  );

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber) => setSkip((pageNumber - 1) * take),
    recordsPerPage: take,
    skip,
    totalRecords: tableData.length,
  }

  const slicedTableData = useMemo(() => {
    return isNameSearchEnabled
      ? tableData
      : tableData.slice(skip, skip + take);
  }, [tableData, skip, isNameSearchEnabled])

  return (
    <>
      <PatternOfCareProviderLookupSearchBox
        handleClear={handleClear}
        handleFieldUpdate={handleFieldUpdate}
        handleSearch={handleSearch}
        searchState={searchState}
        setSearchState={setSearchState}
        isLoading={isLoading}
        isFiltered={isFiltered}
      />

      <Waffles<PatternOfCareProvidersTableModel>
        bulkActionBar={tableData.length > 0 && bulkActionBar}
        columns={columnConfig}
        data={slicedTableData}
        showCheckboxes={true}
        handleCheckboxChange={handleCheckboxChange}
        selectedKeys={selectedProviders}
        testId="pattern-of-care-panel__provider-lookup"
        emptyTableMessage={emptyTableMessage}
        customSort={customSort}
        fullWidth={true}
        // name search is limited to 100, non-name search is not limited
        enablePagination={!isNameSearchEnabled}
        paginationProps={paginationProps}
        totalNumberOfRecords={tableData.length}
      />
    </>
  )
}